/* RegexControlRenderer.js */

"use strict";

const _ = require("lodash");
const RegexUtils = require("./RegexUtils");
const RegexMode = require("./RegexMode");
const TOKEN_COLORS = require("./Tokens");
const MatchSingleLinesMode = require("./modes/MatchSingleLinesMode");
const MatchAllLinesMode = require("./modes/MatchAllLinesMode");
const MatchSectionsMode = require("./modes/MatchSectionsMode");
const AceUtils = require("./AceUtils");
const MATCH_ALL_REGEX = RegexUtils.MATCH_ALL_REGEX;
const EDITOR_ERRORS = {
    quantifier_allows_empty_string_matches:
        "Error: This regular expression matches empty strings. Editor may not be able to highlight all the matches.",
    ambiguous_pipe_reference:
        "Error: Please check your regular expression, it has ambiguous pipe reference ('|') which is preventing from evaluating it correctly.",
};
const THRESHOLD_TEXT_LENGTH_TRIGGERING_IN_PROGRESS_EVENT = 700000;
const IconRegistry = require("@gluenetworks/icon_registry").getInstance();

class RegexControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElement = document.createElement("regex-control-gl");

        this._editorDiv = document.createElement("div");
        this._editorDiv.classList.add("regex-editor");
        this._regexEditorId = _.uniqueId("regex_editor");
        this._editorDiv.setAttribute("id", this._regexEditorId);
        this._namedGroupsDiv = document.createElement("div");
        this._namedGroupsDiv.classList.add("named-groups-details");
        this._namedGroupsDiv.style.display = "none";
        this._rootElement.component = this._model;
        this._errorMessageDiv = document.createElement("div");
        this._errorMessageDiv.classList.add("errors");
        this._errorMessageDiv.textContent = "";
        this._editorHeader = document.createElement("div");
        this._editorHeader.classList.add("editor_header");
        this._editorLabel = document.createElement("label");
        this._editorLabel.setAttribute("for", this._regexEditorId);
        this._editorLabel.textContent = this._model.label;
        this._findPreviousButton = document.createElement("button");
        this._findPreviousButton.classList.add("find-previous");
        this._findPreviousButton.innerHTML = IconRegistry.getRegisteredIcon("common.chevron").icon;
        this._findPreviousButton.title = "Find Previous";
        this._findNextButton = document.createElement("button");
        this._findNextButton.classList.add("find-next");
        this._findNextButton.innerHTML = IconRegistry.getRegisteredIcon("common.chevron").icon;
        this._findNextButton.title = "Find Next";
        this._matchCountDiv = document.createElement("div");
        this._matchCountDiv.classList.add("match_count");
        this._resizeObserver = new ResizeObserver((this._onResize = this._onResize.bind(this)));

        this._buildActionsDiv();
        this._matchCountDetailsStale = false;

        this._registerListeners();
        this.render();
    }

    _buildActionsDiv() {
        this._actionDiv = document.createElement("div");
        this._actionDiv.classList.add("actions");
        this._modeSelect = document.createElement("select");
        this._modeSelect.setAttribute("name", "mode");
        this._modeSelect.setAttribute("id", _.uniqueId("mode_select"));
        this._modeSelect.options[0] = new Option(
            RegexMode.MATCH_SINGLE_LINES.displayValue,
            RegexMode.MATCH_SINGLE_LINES.value,
            true
        );
        this._modeSelect.options[1] = new Option(
            RegexMode.MATCH_ALL_LINES.displayValue,
            RegexMode.MATCH_ALL_LINES.value
        );
        this._modeSelect.options[2] = new Option(
            RegexMode.MATCH_SECTIONS.displayValue,
            RegexMode.MATCH_SECTIONS.value
        );
        this._modeLabel = document.createElement("label");
        this._modeLabel.setAttribute("for", this._modeSelect.getAttribute("id"));
        this._modeLabel.innerHTML = "Mode";
        this._sectionDelimiterDiv = document.createElement("div");
        this._sectionDelimiterDiv.classList.add("sectiondelimiter_action");
        this._sectionDelimiterSelect = document.createElement("select");
        this._sectionDelimiterSelect.setAttribute("id", _.uniqueId("sectiondelimiter_label"));
        this._sectionDelimiterSelect.options[0] = new Option("default", "");
        this._sectionDelimiterSelect.options[1] = new Option("!", "!");
        this._sectionDelimiterLabel = document.createElement("label");
        this._sectionDelimiterLabel.setAttribute(
            "for",
            this._sectionDelimiterSelect.getAttribute("id")
        );
        this._sectionDelimiterLabel.innerHTML = "Section Delimiter";
        this._sectionDelimiterDiv.appendChild(this._sectionDelimiterLabel);
        this._sectionDelimiterDiv.appendChild(this._sectionDelimiterSelect);
        this._actionDiv.appendChild(this._modeLabel);
        this._actionDiv.appendChild(this._modeSelect);
        this._actionDiv.appendChild(this._sectionDelimiterDiv);
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("Regex", (this._onRegex = this._onRegex.bind(this)));
        this._model.on("Mode", (this._onMode = this._onMode.bind(this)));
        this._model.on(
            "SectionDelimiter",
            (this._onSectionDelimiter = this._onSectionDelimiter.bind(this))
        );
        this._model.on(
            "ModeSelectorVisible",
            (this._onModeSelectorVisible = this._onModeSelectorVisible.bind(this))
        );
        this._model.on("ResizeEditor", (this._onResizeEditor = this._onResizeEditor.bind(this)));
        this._model.on("Label", (this._onLabel = this._onLabel.bind(this)));
        this._model.on("AutoResize", (this._onAutoResize = this._onAutoResize.bind(this)));
        this._modeSelect.onchange = this._onModeChangeInUI = this._onModeChangeInUI.bind(this);
        this._sectionDelimiterSelect.onchange = this._onSectionDelimiterChangeInUI =
            this._onSectionDelimiterChangeInUI.bind(this);
        this._findNextButton.addEventListener(
            "click",
            (this._onFindNextButtonClick = this._onFindNextButtonClick.bind(this))
        );
        this._findPreviousButton.addEventListener(
            "click",
            (this._onFindPreviousButtonClick = this._onFindPreviousButtonClick.bind(this))
        );
    }

    _unregisterListeners() {
        this._resizeObserver.disconnect();
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SectionDelimiter", this._onSectionDelimiter);
        this._model.removeListener("ModeSelectorVisible", this._onModeSelectorVisible);
        this._model.removeListener("ResizeEditor", this._onResizeEditor);
        this._model.removeListener("Label", this._onLabel);
        this._model.removeListener("AutoResize", this._onAutoResize);
        this._modeSelect.removeEventListener("change", this._onModeChangeInUI);
        this._sectionDelimiterSelect.removeEventListener(
            "change",
            this._onSectionDelimiterChangeInUI
        );
        this._findNextButton.removeEventListener("click", this._onFindNextButtonClick);
        this._findPreviousButton.removeEventListener("click", this._onFindPreviousButtonClick);
        if (this._regexEditorSession) {
            this._regexEditorSession.removeListener("change", this._onEditorValueChangeDebounce);
            this._regexEditorSession.removeListener("tokenizerUpdate", this._onTokenizerUpdate);
            this._regexEditor.removeListener("mousemove", this.onMouseMove);
            this._regexEditor.removeListener("paste", this.onPaste);
            this._regexEditor.removeListener("changeSelection", this._onChangeSelection);
        }
        this._editorDiv.removeEventListener("mouseleave", this.onMouseLeave);
        if (this._clearNamedGroupsDetailsPopupDebounce) {
            this._regexEditorSession.removeEventListener(
                "changeScrollTop",
                this._clearNamedGroupsDetailsPopupDebounce
            );
            this._clearNamedGroupsDetailsPopupDebounce = undefined;
        }
        if (this._matchAllLinesMode) {
            this._matchAllLinesMode.removeListener("InProgress", this._onInProgressStatusChange);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        if (this._matchAllLinesMode) {
            this._matchAllLinesMode.remove();
            this._matchAllLinesMode = undefined;
        }
        this._regexEditor.destroy();
        this._regexEditor = undefined;
        this._regexEditorSession = undefined;
        this._regexWithMatchingGroups = undefined;
        this._matchCount = undefined;
        this._currentMatchNumber = undefined;
        this._currentSelectionRange = undefined;
        this._matchCountDetailsStale = undefined;
        this._namedGroupsList = [];
        this._resizeObserver = undefined;

        return new Promise((resolve /*, reject*/) => {
            this._editorDiv.remove();
            this._rootElement.remove();
            this._rootElement = undefined;
            resolve();
        });
    }

    render() {
        return new Promise((resolve) => {
            this._setDisabled(this._model.disabled);
            this._setInvalid(this._model.invalid);
            this._setVisible(this._model.visible);
            this._setDarkTheme(this._model.darkTheme);
            this._setMode(this._model.mode);
            this._setSectionDelimiter(this._model.sectionDelimiter);
            this._setModeSelectorVisible(this._model.modeSelectorVisible);
            this._setAutoResize(this._model.autoResize);

            this._rootElement.appendChild(this._errorMessageDiv);
            this._rootElement.appendChild(this._actionDiv);
            this._editorHeader.appendChild(this._findNextButton);
            this._editorHeader.appendChild(this._matchCountDiv);
            this._editorHeader.appendChild(this._findPreviousButton);
            this._editorHeader.appendChild(this._editorLabel);
            this._rootElement.appendChild(this._editorHeader);
            this._rootElement.appendChild(this._editorDiv);
            this._rootElement.appendChild(this._namedGroupsDiv);
            //initialize ace editor
            this._createEditor();
            this._setValue(this._model.value);
            // Listen to changes of text on ace editor document
            this._regexEditorSession.on(
                "change",
                (this._onEditorValueChangeDebounce = _.debounce(
                    this._onEditorValueChange.bind(this),
                    500
                ))
            );
            this._regexEditorSession.on(
                "tokenizerUpdate",
                (this._onTokenizerUpdate = this._onTokenizerUpdate.bind(this))
            );
            if (this._model.autoResize) {
                //gives sometime for the control to be initialized & attached to the parent
                setTimeout(() => {
                    this._autoResizeEditor();
                }, 100);
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElement;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when auto resize property is updated on model
     * @param autoResize
     * @private
     */
    _onAutoResize(autoResize) {
        this._setAutoResize(autoResize);
    }

    /**
     * Fired when root element of this control is resized
     * @private
     */
    _onResize() {
        this._autoResizeEditor();
    }

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onValue(newVal, oldVal, wasTriggeredByUser) {
        if (!wasTriggeredByUser) {
            this._setValue(newVal);
        }
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when darkTheme has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when the regex has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onRegex() {
        this._initializeEditorHighlightRules();
    }

    /**
     * Fired when the mode is updated in the model
     * @private
     */
    _onMode() {
        this._setMode(this._model.mode);
    }

    /**
     * Fired when the sectionDelimiter has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onSectionDelimiter() {
        this._setSectionDelimiter(this._model.sectionDelimiter);
    }

    /**
     * Fired when ModeSelectorVisible has been updated in the model
     */
    _onModeSelectorVisible() {
        this._setModeSelectorVisible(this._model.modeSelectorVisible);
    }

    /**
     * Fired when resizeEditor is invoked on the component with new height
     * @param newHeight {string}
     * @private
     */
    _onResizeEditor(newHeight) {
        let heightInt = parseFloat(newHeight);
        this._setEditorHeight(heightInt);
    }

    /**
     * Fired when label is updated on the component with new value
     * @param newLabel
     * @private
     */
    _onLabel(newLabel) {
        this._setLabel(newLabel);
    }

    /**
     * When text in editor is changed, it triggers UserValue event on the model
     * @param event
     * @private
     */
    _onEditorValueChange(event) {
        let oldVal = this._model.value;
        if (!this._regexEditorSession) {
            return;
        }
        let newVal = this._regexEditorSession.getValue();
        if (
            !this._model.inProgress &&
            this._model.mode === RegexMode.MATCH_ALL_LINES.value &&
            newVal.length > THRESHOLD_TEXT_LENGTH_TRIGGERING_IN_PROGRESS_EVENT
        ) {
            this._model._triggerInProgress(true);
        }
        let timeoutInterval =
            this._model.inProgress && this._model.mode === RegexMode.MATCH_ALL_LINES.value
                ? 500
                : 0;
        setTimeout(() => {
            this._model.setValue(newVal, true);
            this._model._triggerUserValue(newVal, oldVal, event);
            if (
                this._model.mode === RegexMode.MATCH_ALL_LINES.value &&
                this._regexEditorSession &&
                this._matchAllLinesMode
            ) {
                this.updateTooltip();
                this._matchAllLinesMode.addMarkers(
                    this._regexWithMatchingGroups,
                    this._namedGroupsList
                );
                this._updateMatchCount();
            }
            if (
                this._model.inProgress &&
                (this._model.mode !== RegexMode.MATCH_ALL_LINES.value ||
                    this._matchAllLinesMode._renderingInProgress === false)
            ) {
                this._model._triggerInProgress(false);
            }
        }, timeoutInterval);
    }

    _onChangeSelection() {
        this._addFrontMarkersToHighlightSelection();
    }

    /**
     * Invoked when mode is changed by clicking MATCH_SECTIONS Mode check box
     * @private
     */
    _onModeChangeInUI() {
        this._model.mode = this._modeSelect.value;
    }

    /**
     * Invoked when section delimiter is updated on UI
     * @private
     */
    _onSectionDelimiterChangeInUI() {
        this._model.sectionDelimiter = this._sectionDelimiterSelect.value;
    }

    /**
     * Fired when find next button is clicked
     * @private
     */
    _onFindNextButtonClick() {
        this._findNextSearchString();
    }

    /**
     * Fired when find previous button is clicked
     * @private
     */
    _onFindPreviousButtonClick() {
        this._findPreviousSearchString();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */

    /*******************************************/

    _setAutoResize(autoResize) {
        if (autoResize) {
            this._rootElement.classList.add("full-height");
            this._resizeObserver.observe(this._rootElement);
            this._autoResizeEditor();
        } else {
            this._resizeObserver.disconnect();
            this._rootElement.classList.remove("full-height");
        }
    }

    _autoResizeEditor() {
        this._setEditorHeight(this._rootElement.clientHeight);
    }

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal) {
            this._editorDiv.classList.add("disabled");
        } else {
            this._editorDiv.classList.remove("disabled");
        }
        if (this._regexEditor) {
            this._regexEditor.setReadOnly(newVal);
        }
        this._modeSelect.disabled = newVal;
        this._sectionDelimiterSelect.disabled = newVal;
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._rootElement.classList.add("invalid");
        } else {
            this._rootElement.classList.remove("invalid");
        }
    }

    /**
     * Set value of the input
     * @param newVal {string}
     * @private
     */
    _setValue(newVal) {
        if (!this._regexEditorSession) {
            return;
        }
        if (newVal !== this._regexEditorSession.getValue()) {
            if (newVal) {
                this._regexEditorSession.setValue(newVal);
            } else {
                this._regexEditorSession.setValue("");
            }
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElement.classList.remove("hidden");
        } else {
            this._rootElement.classList.add("hidden");
        }
    }

    /**
     * Toggle darkTheme on the control
     * @param newVal
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElement.classList.add("dark");
        } else {
            this._rootElement.classList.remove("dark");
        }
    }

    /**
     * Set or remove focus
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._rootElement.focus();
        } else {
            this._rootElement.blur();
        }
    }

    /**
     * Check cli mode checkbox based on mode value
     * @param newVal {String}
     * @private
     */
    _setMode(newVal) {
        this._modeSelect.value = newVal;
        this._hideSectionDelimiterOnUI(newVal !== RegexMode.MATCH_SECTIONS.value);
        this._initializeEditorHighlightRules();
    }

    _hideSectionDelimiterOnUI(hide) {
        if (hide) {
            this._sectionDelimiterDiv.classList.add("hide");
        } else {
            this._sectionDelimiterDiv.classList.remove("hide");
        }
    }

    _setSectionDelimiter(newVal) {
        this._sectionDelimiterSelect.value =
            this._sectionDelimiterSelect.value !== newVal
                ? newVal
                : this._sectionDelimiterSelect.value;
        this._initializeEditorHighlightRules();
    }

    _setModeSelectorVisible(visible) {
        if (visible) {
            this._actionDiv.style.display = "block";
            this._actionDiv.classList.add("actions");
        } else {
            this._actionDiv.style.display = "none";
            this._actionDiv.classList.remove("actions");
        }
    }

    /**
     * Updates the label on the editor with new value
     * @param newLabel {string}
     * @private
     */
    _setLabel(newLabel) {
        this._editorLabel.textContent = newLabel;
    }

    /**
     * Set regex of the input
     * @param newVal {string}
     * @private
     */
    _setRegex(newVal) {
        this._model.regex = newVal;
        this._initializeEditorHighlightRules();
    }

    /**
     * Highlight next match
     * @private
     */
    _findNextSearchString() {
        this._matchCountDetailsStale =
            !this._currentSelectionRange ||
            !this._regexEditor.getSelectionRange().isEqual(this._currentSelectionRange);
        if (this._model.inProgress || !this._model.regex) {
            this._currentMatchNumber = 0;
        } else if (this._model.mode === RegexMode.MATCH_ALL_LINES.value) {
            this._currentMatchNumber = this._matchAllLinesMode.getNextMatchNumber(
                this._matchCount,
                this._currentMatchNumber,
                this._matchCountDetailsStale
            );
        } else if (this._model.mode === RegexMode.MATCH_SECTIONS.value) {
            this._currentMatchNumber = MatchSectionsMode.getNextMatchNumber(
                this._regexEditor,
                this._model.regex,
                this._currentMatchNumber,
                this._matchCount,
                this._matchCountDetailsStale
            );
        } else if (this._model.mode === RegexMode.MATCH_SINGLE_LINES.value) {
            this._currentMatchNumber = MatchSingleLinesMode.getNextMatchNumber(
                this._regexEditor,
                this._model.regex,
                this._currentMatchNumber,
                this._matchCount,
                this._matchCountDetailsStale
            );
        } else {
            this._currentMatchNumber = 0;
        }
        this._matchCountDetailsStale = false;
        this._currentSelectionRange = this._regexEditor.getSelectionRange();
        this._updateMatchDetails(this._currentMatchNumber, this._matchCount);
    }

    /**
     * Highlight previous match
     * @private
     */
    _findPreviousSearchString() {
        this._matchCountDetailsStale =
            !this._currentSelectionRange ||
            !this._regexEditor.getSelectionRange().isEqual(this._currentSelectionRange);
        if (this._model.inProgress || !this._model.regex) {
            this._currentMatchNumber = 0;
        } else if (this._model.mode === RegexMode.MATCH_ALL_LINES.value) {
            this._currentMatchNumber = this._matchAllLinesMode.getPreviousMatchNumber(
                this._matchCount,
                this._currentMatchNumber,
                this._matchCountDetailsStale
            );
        } else if (this._model.mode === RegexMode.MATCH_SECTIONS.value) {
            this._currentMatchNumber = MatchSectionsMode.getPreviousMatchNumber(
                this._regexEditor,
                this._model.regex,
                this._currentMatchNumber,
                this._matchCount,
                this._matchCountDetailsStale
            );
        } else if (this._model.mode === RegexMode.MATCH_SINGLE_LINES.value) {
            this._currentMatchNumber = MatchSingleLinesMode.getPreviousMatchNumber(
                this._regexEditor,
                this._model.regex,
                this._currentMatchNumber,
                this._matchCount,
                this._matchCountDetailsStale
            );
        } else {
            this._currentMatchNumber = 0;
        }
        this._matchCountDetailsStale = false;
        this._currentSelectionRange = this._regexEditor.getSelectionRange();
        this._updateMatchDetails(this._currentMatchNumber, this._matchCount);
    }

    _addFrontMarkersToHighlightSelection() {
        let selectionRange = this._regexEditor.getSelectionRange();
        let currentFrontMarkers = this._regexEditorSession.getMarkers(true);
        Object.keys(currentFrontMarkers).forEach(function (frontMarkerId) {
            this.removeMarker(frontMarkerId);
        }, this._regexEditorSession);
        this._regexEditorSession.addMarker(selectionRange, "selection_front_marker", "line", true);
    }

    /**
     * checks if the edi
     * @param regex
     * @returns {boolean}
     * @private
     */
    _canEditorFindMatchesForRegex(regex) {
        if (
            MATCH_ALL_REGEX.indexOf(regex) !== -1 ||
            this._model.mode === RegexMode.MATCH_ALL_LINES
        ) {
            return true;
        } else if (
            this._model.mode === RegexMode.MATCH_SINGLE_LINES.value ||
            this._model.mode === RegexMode.MATCH_SECTIONS.value
        ) {
            return AceUtils.canEditorFindMatchesForRegex(regex);
        }
    }

    _addErrorMessageOnUI(errorMessage) {
        this._errorMessageDiv.textContent = errorMessage ? errorMessage : "";
    }

    /**
     * Initializes ace editor & adds highlight rules based on regex value
     * @private
     */
    _createEditor() {
        const ace = require("ace-builds/src-min-noconflict/ace");
        // initialize editor and attach it to div editor class
        this._regexEditor = ace.edit(this._editorDiv, {
            minLines: this._model.minLineCount,
            maxLines: this._model.maxLineCount,
        });
        this._regexEditorSession = this._regexEditor.getSession();
        let TextMode = ace.require("ace/mode/text").Mode;
        let mode = new TextMode();
        this._regexEditorSession.setMode(mode);
        this._initializeEditorHighlightRules();
        this._regexEditor.setReadOnly(this._model._disabled);
    }

    _clearNamedGroupsDetails() {
        while (this._namedGroupsDiv.firstChild) {
            this._namedGroupsDiv.firstChild.remove();
        }
        this._namedGroupsDiv.style.display = "none";
        this._tokenAtMousePositionRange = null;
    }

    /**
     * Adds highlighter rules to editor
     * @private
     */
    _initializeEditorHighlightRules() {
        if (!this._regexEditor) {
            return;
        }
        this._currentMatchNumber = undefined;
        let regex = this._model._regex ? this._model._regex : "";
        let rules = this._getDefaultHighlightRules();
        this._clearMatchAllLinesMode();
        this._unregisterEditorEventListeners();
        this._clearSelectionFrontMarker();
        let canEditorFindMatches = this._canEditorFindMatchesForRegex(regex);
        if (canEditorFindMatches === true) {
            this._addErrorMessageOnUI("");
        } else {
            this._addErrorMessageOnUI(EDITOR_ERRORS[canEditorFindMatches]);
        }

        if (regex) {
            let namedGroupRegex = new RegExp("(?<=\\()\\?<(?<groupName>[^>]+)>", "g");
            let regexWithoutUserGroups = regex;
            this._namedGroupsList = [];
            if (regex.match(namedGroupRegex) !== null) {
                this._regexWithMatchingGroups = RegexUtils.getRegexWithMatchingGroups(regex, true);
                regexWithoutUserGroups = this._regexWithMatchingGroups.replace(namedGroupRegex, "");
                this._namedGroupsList = Array.from(
                    this._regexWithMatchingGroups.matchAll(namedGroupRegex)
                ).map(function (match) {
                    return match.groups.groupName;
                });
            } else {
                this._regexWithMatchingGroups = regex;
            }

            if (this._model.mode === RegexMode.MATCH_SINGLE_LINES.value) {
                rules = MatchSingleLinesMode.getHighlightRules(
                    regexWithoutUserGroups,
                    this._namedGroupsList,
                    TOKEN_COLORS
                );
            } else if (this._model.mode === RegexMode.MATCH_ALL_LINES.value) {
                this._matchAllLinesMode = this._matchAllLinesMode
                    ? this._matchAllLinesMode
                    : new MatchAllLinesMode(this._regexEditor);
                this._matchAllLinesMode.on(
                    "InProgress",
                    (this._onInProgressStatusChange = this._onInProgressStatusChange.bind(this))
                );
                this._matchAllLinesMode.addMarkers(
                    this._regexWithMatchingGroups,
                    this._namedGroupsList
                );
                this._updateMatchCount();
            } else if (this._model.mode === RegexMode.MATCH_SECTIONS.value) {
                rules = MatchSectionsMode.getHighlightRules(
                    regexWithoutUserGroups,
                    this._model.sectionDelimiter
                );
            }
            this._registerEventListenersOnEditorRulesUpdate();
        }
        this._applyHighlightRulesOnEditor(rules);
    }

    _registerEventListenersOnEditorRulesUpdate() {
        if (
            (this.model.mode === RegexMode.MATCH_SINGLE_LINES.value ||
                this.model.mode === RegexMode.MATCH_ALL_LINES.value) &&
            !_.isEmpty(this._namedGroupsList)
        ) {
            this._regexEditor.on("mousemove", (this.onMouseMove = this.onMouseMove.bind(this)));
            this._editorDiv.addEventListener(
                "mouseleave",
                (this.onMouseLeave = this.onMouseLeave.bind(this))
            );
            this._regexEditor.on("paste", (this.onPaste = this.onPaste.bind(this)));
            this._clearNamedGroupsDetailsPopupDebounce = _.debounce(
                (this._clearNamedGroupsDetails = this._clearNamedGroupsDetails.bind(this)),
                100,
                {
                    leading: true,
                    trailing: false,
                }
            );
            this._regexEditorSession.addEventListener(
                "changeScrollTop",
                this._clearNamedGroupsDetailsPopupDebounce
            );
        }
        if (this.model.mode !== RegexMode.MATCH_ALL_LINES.value) {
            this._regexEditor.addEventListener(
                "changeSelection",
                (this._onChangeSelection = this._onChangeSelection.bind(this))
            );
            this._onChangeSelection();
        }
    }

    _unregisterEditorEventListeners() {
        this._clearNamedGroupsDetails();
        this._regexEditor.removeEventListener("mousemove", this.onMouseMove);
        this._regexEditor.removeEventListener("paste", this.onPaste);
        this._editorDiv.removeEventListener("mouseleave", this.onMouseLeave);
        this._regexEditorSession.removeEventListener(
            "changeScrollTop",
            this._clearNamedGroupsDetailsPopupDebounce
        );
        this._clearNamedGroupsDetailsPopupDebounce = undefined;
        this._regexEditor.removeListener("changeSelection", this._onChangeSelection);
    }

    onMouseMove(e) {
        if (_.isEmpty(this._model.regex)) {
            return;
        }

        let position = e.getDocumentPosition();
        if (position) {
            let tooltipHtml;
            if (
                this._tokenAtMousePositionRange &&
                this._tokenAtMousePositionRange.contains(position.row, position.column)
            ) {
                this._updateTooltipPosition(position);
            }
            if (this._model.mode === RegexMode.MATCH_SINGLE_LINES.value) {
                let tokenAtMousePosition = this._getTokenAtPosition(position);
                if (
                    tokenAtMousePosition &&
                    (tokenAtMousePosition.type.startsWith("matched_section") ||
                        tokenAtMousePosition.type.startsWith("namedgroup_"))
                ) {
                    tooltipHtml = this.getTooltipText(tokenAtMousePosition, position.row + 1);
                    let tokenEndPosition = this._regexEditorSession
                        .getDocument()
                        .indexToPosition(
                            tokenAtMousePosition.start + tokenAtMousePosition.value.length - 1,
                            position.row
                        );
                    this._tokenAtMousePositionRange = AceUtils.getRangeInstance(
                        position.row,
                        tokenAtMousePosition.start,
                        tokenEndPosition.row,
                        tokenEndPosition.column
                    );
                } else {
                    this._tokenAtMousePositionRange = null;
                }
            } else if (this._model.mode === RegexMode.MATCH_ALL_LINES.value) {
                let matchDetailsAtMousePosition =
                    this._matchAllLinesMode.getMatchDetailsAtPosition(position);
                if (matchDetailsAtMousePosition) {
                    tooltipHtml = this.getTooltipTextInMatchAllLinesMode(
                        matchDetailsAtMousePosition
                    );
                }
                this._tokenAtMousePositionRange = matchDetailsAtMousePosition
                    ? matchDetailsAtMousePosition.range
                    : null;
            }
            if (tooltipHtml) {
                this._pixelPosition = {pageX: e.domEvent.pageX, pageY: e.domEvent.pageY};
                this.updateTooltip(this._pixelPosition, tooltipHtml);
            } else {
                this.updateTooltip(this._regexEditor.renderer.textToScreenCoordinates(position));
            }
        } else {
            this._clearNamedGroupsDetails();
        }
    }

    onPaste(e) {
        if (
            this._model.mode === RegexMode.MATCH_ALL_LINES.value &&
            e.text.length > THRESHOLD_TEXT_LENGTH_TRIGGERING_IN_PROGRESS_EVENT
        ) {
            this._model._triggerInProgress(true);
        }
    }

    _hideNamedDetailsPopup() {
        this.updateTooltip();
    }

    _onInProgressStatusChange(inProgress) {
        // set editor to disabled if in progress else enable the editor
        this._setDisabled(inProgress);
        this._model._triggerInProgress(inProgress);
        if (!inProgress && this._model.mode === RegexMode.MATCH_ALL_LINES.value) {
            this._updateMatchCount();
        }
    }

    _onTokenizerUpdate() {
        if (this._model.mode !== RegexMode.MATCH_ALL_LINES) {
            this._updateMatchCount();
        }
    }

    _getTokenAtPosition(position) {
        let currentLineText = this._regexEditorSession.getLine(position.row);
        let regexP = new RegExp(this._model.regex, "g");
        let token = null,
            currMatch = regexP.exec(currentLineText),
            prevMatch = null;
        while (!token && !_.isNil(currMatch) && regexP.lastIndex <= currentLineText.length) {
            token =
                position.column >= currMatch.index &&
                position.column < currMatch.index + _.get(currMatch[0], "length", 0)
                    ? {
                          value: currMatch[0],
                          type: "namedgroup_all",
                          start: currMatch.index,
                      }
                    : null;
            if (!token) {
                if (_.isEmpty(currMatch[0] && prevMatch && currMatch.index === prevMatch.index)) {
                    regexP.lastIndex++;
                }
                prevMatch = currMatch;
                currMatch = regexP.exec(currentLineText);
            }
        }
        return token;
    }

    onMouseLeave() {
        this._clearNamedGroupsDetails();
    }

    getTooltipText(token, lineNumber) {
        if (!token.type.startsWith("matched_section") && !token.type.startsWith("namedgroup_")) {
            return "";
        }
        let matchDetailsHtml = "";
        matchDetailsHtml += "<table>";
        matchDetailsHtml +=
            "<tr><th></th><th></th><th>Col #</th><th>Line #</th><th>Length</th></tr>";
        let regex = new RegExp(this._regexWithMatchingGroups, "g");
        let matchOnToken = regex.exec(token.value);
        let groups = matchOnToken && matchOnToken.groups ? matchOnToken.groups : undefined;
        let tokenLength = token.value.length;
        if (groups) {
            matchDetailsHtml +=
                "<tr><td>Match:</td>" +
                "<td><span>" +
                token.value +
                "</span></td>" +
                "<td>" +
                (token.start + 1) +
                "</td>" +
                "<td>" +
                tokenLength +
                "</td></tr>";
            matchDetailsHtml += this.buildTooltipNamedGroupsHtml(matchOnToken, {
                start: {row: lineNumber - 1, column: token.start},
                end: {row: lineNumber - 1, column: token.start + tokenLength},
            });
            matchDetailsHtml += "</table>";
            matchDetailsHtml +=
                '<div class="namedGroupsFooter">Line Number: ' + lineNumber + "</div>";
        }
        return matchDetailsHtml;
    }

    getTooltipTextInMatchAllLinesMode(matchDetails) {
        if (!matchDetails) {
            return "";
        }
        let matchDetailsHtml = "";
        matchDetailsHtml += "<table>";
        matchDetailsHtml +=
            "<tr><th></th><th></th><th>Col #</th><th>Line #</th><th>Length</th></tr>";
        let groups = _.get(matchDetails, "match.groups", undefined);
        if (groups) {
            matchDetailsHtml +=
                "<tr><td>Match:</td>" +
                "<td><span>" +
                matchDetails.match[0] +
                "</span></td>" +
                "<td>" +
                (matchDetails.range.start.column + 1) +
                "</td>" +
                "<td>" +
                matchDetails.match[0].length +
                "</td></tr>";
            matchDetailsHtml += this.buildTooltipNamedGroupsHtml(
                matchDetails.match,
                matchDetails.range
            );
            matchDetailsHtml += "</table>";
            matchDetailsHtml +=
                '<div class="namedGroupsFooter">Line(s) : ' + (matchDetails.range.start.row + 1);
            matchDetailsHtml +=
                matchDetails.range.start.row !== matchDetails.range.end.row
                    ? "-" + (matchDetails.range.end.row + 1)
                    : "";
            matchDetailsHtml += "</div>";
            return matchDetailsHtml;
        }
    }

    buildTooltipNamedGroupsHtml(match, matchRange) {
        let namedGroupsDetails = RegexUtils.getNamedGroupDetails(
            match,
            this._namedGroupsList,
            TOKEN_COLORS
        );
        let tokenCount = 0;
        let matchDetailsHtml = "";
        let multilineMatch = matchRange.start.row !== matchRange.end.row;
        for (let i = 0; i < namedGroupsDetails.length; i++) {
            if (namedGroupsDetails[i].name.startsWith("$$AddedGroup")) {
                // do not add this to tooltip
            } else if (namedGroupsDetails[i].value) {
                let token = TOKEN_COLORS[tokenCount % TOKEN_COLORS.length];
                matchDetailsHtml +=
                    '<tr><td>Group "' +
                    namedGroupsDetails[i].name +
                    '": </td><td><span class="ace_' +
                    token +
                    '">' +
                    namedGroupsDetails[i].value +
                    "</span></td>";
                if (multilineMatch) {
                    let position = this._regexEditorSession
                        .getDocument()
                        .indexToPosition(
                            matchRange.start.column + namedGroupsDetails[i].startIndex,
                            matchRange.start.row
                        );
                    matchDetailsHtml += "<td>" + (position.column + 1) + "</td>";
                    matchDetailsHtml += "<td>" + (position.row + 1) + "</td>";
                } else {
                    matchDetailsHtml +=
                        "<td>" +
                        (namedGroupsDetails[i].startIndex + matchRange.start.column + 1) +
                        "</td>";
                    matchDetailsHtml += "<td>" + (matchRange.start.row + 1) + "</td>";
                }
                matchDetailsHtml += "<td>" + namedGroupsDetails[i].value.length + "</td></tr>";
                tokenCount++;
            } else {
                matchDetailsHtml +=
                    '<tr><td>Group "' +
                    namedGroupsDetails[i].name +
                    '": </td><td></td>' +
                    "<td></td>" +
                    "<td></td>" +
                    "<td>0</td></tr>";
            }
        }
        return matchDetailsHtml;
    }

    updateTooltip(position, html) {
        if (html) {
            this._updateTooltipPosition(position);
            this._namedGroupsDiv.style.display = "block";
            this._namedGroupsDiv.innerHTML = html;
        } else {
            this._namedGroupsDiv.style.display = "none";
            while (this._namedGroupsDiv.firstChild) {
                this._namedGroupsDiv.firstChild.remove();
            }
        }
    }

    _updateTooltipPosition(position) {
        if (position) {
            this._namedGroupsDiv.style.left = position.pageX + "px";
            this._namedGroupsDiv.style.top = position.pageY + "px";
        }
    }

    _getDefaultHighlightRules() {
        let rules = {
            start: [
                {
                    token: "empty_line",
                    regex: "^$",
                },
                {
                    defaultToken: "text",
                },
            ],
        };
        return rules;
    }

    _clearMatchAllLinesMode() {
        if (this._matchAllLinesMode) {
            this._matchAllLinesMode.removeListener("InProgress", this._onInProgressStatusChange);
            this._matchAllLinesMode.clear();
        }
    }

    /**
     * Applies given highlight rules on the editor
     * @param rules - text highight rules object
     * @private
     */
    _applyHighlightRulesOnEditor(rules) {
        let mode = this._regexEditorSession.$mode;
        let tokenizer = AceUtils.getTokenizerInstance(rules);
        mode.$tokenizer = tokenizer;
        this._regexEditorSession.bgTokenizer.setTokenizer(mode.getTokenizer());
    }

    _updateMatchCount() {
        this._matchCount = 0;
        this._currentMatchNumber = undefined;
        // if current selection is valid match of given regex, update current match number (this._currentMatchNumber) in this method
        // else delay updating current match number till user clicks on next/previous buttons
        let isCurrentSelectionValidMatch = this._isValidMatch(
            this._regexEditor.getSelectedText(),
            this._model.regex
        );
        if (this._model.inProgress || !this._model.regex) {
            this._matchCount = 0;
        } else if (this._model.mode === RegexMode.MATCH_ALL_LINES.value) {
            this._matchCount = this._matchAllLinesMode.getMatchCount();
            this._currentMatchNumber = isCurrentSelectionValidMatch
                ? this._matchAllLinesMode.getCurrentMatchNumber(
                      this._regexEditor.getSelectionRange().end
                  )
                : undefined;
        } else if (this._model.mode === RegexMode.MATCH_SECTIONS.value) {
            this._matchCount = MatchSectionsMode.getMatchCount(this._regexEditorSession);
            this._currentMatchNumber = isCurrentSelectionValidMatch
                ? MatchSectionsMode.getCurrentMatchNumber(this._regexEditor, this._model.regex)
                      .matchCount
                : undefined;
        } else if (this._model.mode === RegexMode.MATCH_SINGLE_LINES.value) {
            this._matchCount = MatchSingleLinesMode.getMatchCount(
                this._regexEditorSession,
                this._model.regex
            );
            this._currentMatchNumber = isCurrentSelectionValidMatch
                ? MatchSingleLinesMode.getCurrentMatchNumber(
                      this._regexEditor,
                      this._regexEditorSession,
                      this._regexEditor.getSelectionRange().end,
                      this._model.regex
                  )
                : undefined;
        } else {
            this._matchCount = 0;
        }
        this._matchCountDetailsStale = isCurrentSelectionValidMatch;
        this._updateMatchDetails(this._currentMatchNumber, this._matchCount);
    }

    _isValidMatch(text, regex) {
        if (!text || !regex) {
            return false;
        }
        let match = text.match(regex);
        return match ? match[0] === text : false;
    }

    _updateMatchDetails(currentMatchNumber, totalMatchCount) {
        if (totalMatchCount > 0) {
            if (currentMatchNumber) {
                this._matchCountDiv.innerText = currentMatchNumber + "/" + totalMatchCount;
                this._matchCountDiv.style["min-width"] =
                    ((totalMatchCount + "").length * 2 + 1) * 0.633 + 0.4 + "rem";
            } else {
                this._matchCountDiv.innerText = totalMatchCount;
                this._matchCountDiv.style["min-width"] =
                    (totalMatchCount + "").length * 0.633 + 0.4 + "rem";
            }
            this._findNextButton.classList.remove("hide");
            this._findPreviousButton.classList.remove("hide");
        } else {
            this._matchCountDiv.innerText = "No matches";
            this._findNextButton.classList.add("hide");
            this._findPreviousButton.classList.add("hide");
        }
    }

    _clearSelectionFrontMarker() {
        let existingMarkers = this._regexEditorSession.getMarkers(true);
        Object.keys(existingMarkers).forEach(function (existingMarker) {
            this.removeMarker(existingMarker);
        }, this._regexEditorSession);
        this._currentSelectionRange = undefined;
    }

    _setEditorHeight(newHeight) {
        if (!isNaN(newHeight) && this._regexEditor) {
            let headerAndOtherElementsHeight = 70;
            let modeSelectorHeight = this._model.modeSelectorVisible ? 20 : 0;
            let minHeight = 120 - (headerAndOtherElementsHeight - modeSelectorHeight);
            let editorHeight = newHeight - (headerAndOtherElementsHeight + modeSelectorHeight);
            editorHeight = editorHeight > minHeight ? editorHeight : minHeight;
            this._regexEditor.setOptions({minLines: null, maxLines: null});
            this._editorDiv.style.height = editorHeight + "px";
            this._regexEditor.resize();
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = RegexControlRenderer;
